const shuffleArray = (a) => {
	var j, x, i;
	for(i = a.length - 1; i > 0; i--) {
		j = Math.floor(Math.random() * (i + 1));
		x = a[i];
		a[i] = a[j];
		a[j] = x;
	}
	return a;
};

export enum Direction {
	RIGHT,
	DOWN,
	DOWN_RIGHT,
	UP_RIGHT,
	LEFT,
	UP,
	DOWN_LEFT,
	UP_LEFT
}

enum Level {
	EASY = 'easy',
	NORMAL = 'normal',
	HARD = 'hard'
}

interface WordSearchConfig {
	columns:number,
	level:Level,
	words:string[]
	fillBlanks?:Boolean
	characterSet?:string[]
}

interface Stack {
	grid:WordSearch,
	word:string,
	directions:Direction[],
	positions:number[]
}

interface Word {
	word:string,
	start:number[],
	end:number[],
	ok_letters:object,
	orientation:string
}

const DIRS = {
	[Direction.RIGHT]:[0,1],
	[Direction.DOWN]:[1,0],
	[Direction.DOWN_RIGHT]:[1,1],
	[Direction.UP_RIGHT]:[-1,1],
	[Direction.LEFT]:[0,-1],
	[Direction.UP]:[-1,0],
	[Direction.UP_LEFT]:[-1,-1],
	[Direction.DOWN_LEFT]:[1,-1],
}


const DIR_NAMES = {
	[Direction.RIGHT]:'horizontal',
	[Direction.DOWN]:'vertical',
	[Direction.DOWN_RIGHT]:'diagonal',
	[Direction.UP_RIGHT]:'diagonalUp',
	[Direction.LEFT]:'horizontalBack',
	[Direction.UP]:'verticalUp',
	[Direction.UP_LEFT]:'diagonalUpBack',
	[Direction.DOWN_LEFT]:'diagonalBack',
}


export class WordSearch {

	rows:number;
	columns:number;
	grid:string[];
	loops:number;
	words:Word[];


	constructor(rows,columns,grid=undefined) {
		this.rows = rows;
		this.words = [];
		this.loops = 0;
		this.columns = columns;
		this.grid = (grid != undefined) ? grid : (new Array(this.rows*this.columns)).fill("");
	}

	public getColumns() {
		return this.columns;
	}

	public getGrid() {
		return this.grid;
	}

	public getPos(pos:number){
		return this.grid[pos];
	}
	
	public setPos(pos:number,value:string) {
		this.grid[pos] = value;
	}

	public fill(characters:string[]) {
		const random_chars = shuffleArray(characters);
		const length = random_chars.length;
		this.grid = this.grid.map(letter=>{
			if (letter != "") return letter;
			const random = Math.floor(Math.random()*length);
			return random_chars[random];

		})  
	}

	public at(pos:number) {
		const row = Math.floor(pos / this.columns);
		const col = pos % this.columns;
		return [row,col]
	}

	public getRowCol(row:number,col:number) {
		return this.grid[row*this.columns+col];
	}

	public setRowCol(row:number,col:number,value:string) {
		this.setPos(row*this.columns+col,value);
	}

	public getCopy() {
		const ws = new WordSearch(this.rows,this.columns,[...this.grid]);
		ws.words = [...this.words];
		return ws;
	}

	public get2D() {
		const array_2d = [];
		for(var i = 0; i<this.rows; i++){
			array_2d.push([]);
			for (var j = 0; j<this.columns; j++) {
				array_2d[i][j] = this.getRowCol(i,j);
			}
		}
		return array_2d;
	}

	public addWord(word:Word) {
		this.words.push(word);
	}

	public getWords() {
		return this.words;
	}
}

const generateWordSearch = (config:WordSearchConfig) => {

	const available_positions = Array.from({length: config.columns*config.columns}, (_, index) => index + 1);
	const available_directions = [Direction.RIGHT,Direction.DOWN];
	const available_words = [...config.words];

	const available_characters = config?.characterSet ?? ['a','b','c','d','e','f','g','h','i','j','k','l','m','n','o','p','q','r','s','t','u','v','w','x','y','z'];

	let max_loops = 10000;

	if (config.level == Level.NORMAL) {
		available_directions.push(Direction.DOWN_RIGHT,Direction.UP_RIGHT);
	}

	if (config.level == Level.HARD) {
		available_directions.push(Direction.DOWN_RIGHT,Direction.UP_RIGHT,Direction.UP,Direction.UP_LEFT,Direction.DOWN_LEFT,Direction.UP_LEFT);
	}

	const word = available_words.pop();
	if (word == undefined) return false;
	const stack : Stack[] = [
		{
			grid: new WordSearch(config.columns,config.columns),
			word: word,
			directions: shuffleArray([...available_directions]),
			positions: shuffleArray([...available_positions])
		}
	]

	while (max_loops > 0) {

		const current = stack[stack.length-1];

		if (current.directions.length == 0) {
			current.positions.pop();
			current.directions = shuffleArray([...available_directions]);
		}
		const direction = current.directions.pop();
		
		if (current.positions.length == 0) {
			available_words.unshift(current.word)
			stack.pop();
		}
		else {
			const position = current.positions.pop();
			const grid = try_word(current.grid,current.word,position,direction);

			if (grid ) {
				grid.loops = max_loops;
				
				if (available_words.length > 0) {
					stack.push({
						grid,
						word:available_words.pop(),
						directions: shuffleArray([...available_directions]),
						positions: shuffleArray([...available_positions])
					})
				}
				else {
					if (config?.fillBlanks ?? false) {
						grid.fill(available_characters);
					}
					return grid;
				}
			}
		}
		max_loops--;
	}
}

export const try_word = (grid:WordSearch,word:string,position:number,direction:Direction) => {
	const new_grid = grid.getCopy();
	let [row,col] = new_grid.at(position);
	const start = [col,row];
	const end = [0,0];
	const ok_letters = {};
	if (ok_letters[col] == undefined) ok_letters[col] = {};
	ok_letters[col][row] = 1;

	const [direction_row,direction_column] = DIRS[direction];

	const letters = word.split("");
	const limit = grid.getColumns();
	while (row >= 0 && row < limit && col >=0 && col<limit) {
		const letter = letters.shift();
		if (!letter) break;
		const current_pos = new_grid.getRowCol(row,col);
		if (current_pos == '' || current_pos == letter) {
			new_grid.setRowCol(row,col,letter);
			end[0] = col;
			end[1] = row;
			if (ok_letters[col] == undefined) ok_letters[col] = {};
			ok_letters[col][row] = 1;
			row += direction_row;
			col += direction_column;
		}
		else {
			return false;
		}
	}
	if (letters.length >0) return false;
	new_grid.addWord({
		word:word,
		start,
		end,
		ok_letters,
		orientation:DIR_NAMES[direction]
	})

	return new_grid;
}

export default generateWordSearch;